/**
 * Created by neo on 11.01.2026
 * Activity Log Screen - View and manage activity history
 */
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Container } from 'reactstrap';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { ActivityLog, ActivityLogApi } from '../../Model/Activity/ActivityLog';
import { Button } from '../../Components/Button';
import { LoadingContainer } from '../../Components/LoadingContainer';
import { ActivityLogFormModal } from './Components/ActivityLogFormModal';
import { getActivityTypeById } from '../../Config/ActivityTypes';
import { Theme } from '../../Theme/Theme';

dayjs.extend(relativeTime);

const PageContainer = styled(Container)`
  padding-top: 32px;
  padding-bottom: 48px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
`;

const ActivitiesGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const ActivityCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ActivityIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
`;

const ActivityInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 4px;
`;

const ActivityDate = styled.div`
  font-size: 14px;
  color: ${Theme.Colors.warmGrey};
`;

const ActivityStats = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${Theme.Colors.primary};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
  text-transform: uppercase;
  margin-top: 2px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const EmptyEmoji = styled.span`
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 24px 0;
`;

export const ActivityLogScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const { authentication } = useRootStore();

  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);

  const fetchActivities = useCallback(async () => {
    if (!authentication.athlete) return;

    setLoading(true);
    try {
      const fetchedActivities = await ActivityLogApi.find({
        athleteId: authentication.athlete.id,
        page: 0,
        size: 50,
        sort: 'startDate,DESC',
      });
      setActivities(fetchedActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  }, [authentication.athlete]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleActivityLogged = useCallback(
    (activity: ActivityLog) => {
      // Add to the beginning of the list
      setActivities((prev) => [activity, ...prev]);
    },
    [],
  );

  const formatDuration = useCallback((durationMinutes: number): string => {
    const hours = Math.floor(durationMinutes / 60);
    const mins = Math.round(durationMinutes % 60);

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }, []);

  if (loading) {
    return <LoadingContainer text={t('activity.loading', { defaultValue: 'Loading activities...' })} />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>{t('activity.title', { defaultValue: 'My Activities' })}</PageTitle>
        <Button color="primary" onClick={() => setFormModalOpen(true)}>
          {t('activity.logNewButton', { defaultValue: 'Log New Activity' })}
        </Button>
      </PageHeader>

      {activities.length === 0 ? (
        <EmptyState>
          <EmptyEmoji>🏃</EmptyEmoji>
          <EmptyTitle>{t('activity.empty.title', { defaultValue: 'No activities yet' })}</EmptyTitle>
          <EmptyDescription>
            {t('activity.empty.description', {
              defaultValue: 'Start logging your activities to track your progress and earn points!',
            })}
          </EmptyDescription>
          <Button color="primary" onClick={() => setFormModalOpen(true)}>
            {t('activity.logFirstButton', { defaultValue: 'Log Your First Activity' })}
          </Button>
        </EmptyState>
      ) : (
        <ActivitiesGrid>
          {activities.map((activity) => {
            const activityType = getActivityTypeById(activity.type || 'other');
            const durationMinutes = activity.data['DURATION'] || 0;
            const distance = activity.data['DISTANCE'];
            const calories = activity.data['CALORIES'];

            return (
              <ActivityCard key={activity.id}>
                <ActivityIcon>{activityType?.icon || '🏃'}</ActivityIcon>
                <ActivityInfo>
                  <ActivityName>{activityType?.name || t('activity.unknown', { defaultValue: 'Activity' })}</ActivityName>
                  <ActivityDate>
                    {dayjs(activity.startDate).format('MMM D, YYYY [at] HH:mm')} •{' '}
                    {dayjs(activity.startDate).fromNow()}
                  </ActivityDate>
                </ActivityInfo>
                <ActivityStats>
                  {durationMinutes > 0 && (
                    <StatItem>
                      <StatValue>{formatDuration(durationMinutes)}</StatValue>
                      <StatLabel>{t('activity.stats.duration', { defaultValue: 'Duration' })}</StatLabel>
                    </StatItem>
                  )}
                  {distance && distance > 0 && (
                    <StatItem>
                      <StatValue>{distance.toFixed(1)}</StatValue>
                      <StatLabel>{t('activity.stats.km', { defaultValue: 'km' })}</StatLabel>
                    </StatItem>
                  )}
                  {calories && calories > 0 && (
                    <StatItem>
                      <StatValue>{Math.round(calories)}</StatValue>
                      <StatLabel>{t('activity.stats.calories', { defaultValue: 'cal' })}</StatLabel>
                    </StatItem>
                  )}
                </ActivityStats>
              </ActivityCard>
            );
          })}
        </ActivitiesGrid>
      )}

      <ActivityLogFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onActivityLogged={handleActivityLogged}
      />
    </PageContainer>
  );
});
